import React from "react";
import { ImprovedProduct } from "../../Interfaces/Product";
import {
  DesignFile,
  ImprovedParticipantShop,
} from "../../Interfaces/FundraiserRole";
import ProductCardGrid from "./ProductCardGrid";
import DesignCardGrid from "./DesignCardGrid";
import { ITEMS_PER_PAGE, ITEMS_PER_PAGE_DESIGNS } from "./constants";
import { Category } from "../../CommonFunctions";
interface DesignOrProductGridProps {
  showDesktopDesignFilters: boolean;
  designCategories: string[];
  selectedDesignCategories: string[];
  designs: any[];
  filteredProducts: ImprovedProduct[];
  paginatedProducts: ImprovedProduct[];
  participantShop: ImprovedParticipantShop;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  currentDesignPage: number;
  setCurrentDesignPage: (page: number) => void;
  selectedCategory: Category | string;
  isFromSamplePage?: boolean;
}

const DesignOrProductGrid: React.FC<DesignOrProductGridProps> = ({
  showDesktopDesignFilters,
  designCategories,
  selectedDesignCategories,
  designs,
  filteredProducts,
  paginatedProducts,
  participantShop,
  currentPage,
  setCurrentPage,
  currentDesignPage,
  setCurrentDesignPage,
  selectedCategory,
  isFromSamplePage = false,
}) => {
  const [filteredDesigns, setFilteredDesigns] = React.useState<any[]>([]);
  const showDesigns = designCategories.length > 0 && designs.length > 0;
  const totalProductPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const totalDesignPages = Math.ceil(
    filteredDesigns.length / ITEMS_PER_PAGE_DESIGNS,
  );

  React.useEffect(() => {
    if (!designs || designs.length === 0) {
      setFilteredDesigns([]);
      return;
    }

    if (selectedDesignCategories.length === 0) {
      setFilteredDesigns(designs);
      return;
    }

    interface DesignCategory {
      name?: string;
      sub_cat?: string;
    }

    const allDesignsWithCategories = designs.map((design) => {
      if (!design.extra_info) {
        return { design, categories: [] };
      }

      const categories: string[] = [];

      if (Array.isArray(design.extra_info.categories)) {
        (design.extra_info.categories as DesignCategory[]).forEach(
          (cat: DesignCategory) => {
            if (cat && typeof cat === "object") {
              if (cat.name) {
                categories.push(cat.name);
              }
              if (cat.sub_cat) {
                categories.push(cat.sub_cat);
              }
            }
          },
        );
      } else if (design.extra_info.category) {
        categories.push(design.extra_info.category);
      }

      const uniqueCategories = [...new Set(categories)].filter(Boolean);

      return {
        design,
        categories: uniqueCategories,
      };
    });

    let matchingDesigns = allDesignsWithCategories
      .filter(({ categories }) => {
        if (categories.length === 0) {
          return selectedDesignCategories.length === 0;
        }

        const designCategories = categories.map((cat) => cat.toLowerCase());
        const selectedCategories = selectedDesignCategories.map((cat) =>
          cat.toLowerCase(),
        );

        return designCategories.some((designCat) =>
          selectedCategories.includes(designCat),
        );
      })
      .map(({ design }) => design);

    matchingDesigns = [...matchingDesigns].sort(
      (a, b) =>
        Number(!!(b.fundraiser || b.fundraiser_id)) -
        Number(!!(a.fundraiser || a.fundraiser_id)),
    );

    setFilteredDesigns(matchingDesigns);
    setCurrentDesignPage(1);
  }, [selectedDesignCategories, designs]);

  const isProductCategoryWithDesigns = (
    productCategory: Category | string,
  ): boolean => {
    return (
      productCategory.includes("Custom") ||
      productCategory.includes("Organization")
    );
  };

  const getFirstProduct = (design: DesignFile) => {
    const firstAvailableProduct = participantShop.products?.find((p) => p);

    if (
      selectedCategory &&
      participantShop.sku_by_category[selectedCategory]?.[0]
    ) {
      const sku = participantShop.sku_by_category[selectedCategory][0];
      const foundProduct = participantShop.products?.find((p) => p.sku === sku);
      if (foundProduct) {
        return foundProduct;
      }
    }

    return (
      firstAvailableProduct || {
        sku: 111,
        name: design?.file_name || "Custom Design",
        description: design?.file_name || "Custom design product",
        base_price: 100,
        images: design?.file_location ? [design.file_location] : [],
        variants: { style_variants: [], color_variants: [] },
        is_customizable: true,
        features: [],
      }
    );
  };

  const paginatedFilteredDesigns = React.useMemo(() => {
    const startIndex = (currentDesignPage - 1) * ITEMS_PER_PAGE_DESIGNS;
    return filteredDesigns.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE_DESIGNS,
    );
  }, [filteredDesigns, currentDesignPage]);

  const productGrid = () => {
    return (
      <div className={`col-12 ${showDesktopDesignFilters ? "col-md-9" : ""}`}>
        <ProductCardGrid
          paginatedProducts={paginatedProducts}
          participantShop={participantShop}
          totalProductPages={totalProductPages}
          currentPage={currentPage}
          isFromSamplePage={isFromSamplePage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    );
  };
  if (!showDesigns || filteredProducts.length > 0) {
    if (isProductCategoryWithDesigns(selectedCategory)) {
      return productGrid();
    } else {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          {productGrid()}
        </div>
      );
    }
  }

  if (showDesigns) {
    return (
      <div className={`col-12 ${showDesktopDesignFilters ? "col-md-9" : ""}`}>
        <DesignCardGrid
          paginatedDesigns={paginatedFilteredDesigns}
          getFirstProduct={getFirstProduct}
          participantShop={participantShop}
          totalDesignPages={totalDesignPages}
          currentPage={currentDesignPage}
          isFromSamplePage={isFromSamplePage}
          onPageChange={(page) => {
            setCurrentDesignPage(page);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      </div>
    );
  }

  return (
    <div className={`col-12 ${showDesktopDesignFilters ? "col-md-9" : ""}`}>
      <div className="text-center py-5">
        <h4>No products found in this category</h4>
        <p>Please try selecting a different category</p>
      </div>
    </div>
  );
};

export default DesignOrProductGrid;
